/**
 * Main application router
 */

import { useEffect } from 'react'
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/core/auth/AuthContext'
import LoginPage from '@/core/auth/LoginPage'
import LoadingPage from '@/shared/components/LoadingPage'
import Layout from '@/shared/components/layout/Layout'
import DashboardPage from '@/modules/dashboard/DashboardPage'
import LeaveRoutes from '@/modules/leave'
import LMSRoutes from '@/modules/lms'
import AdminRoutes from '@/modules/admin'
import ReimbursementRoutes from '@/modules/reimbursement'
import LearningRoutes from '@/modules/learning'
import EmployeeInfoRoutes from '@/modules/employee-info'
import LunchExpenseRoutes from '@/modules/lunch-expense'
import PenilaianRoutes from '@/modules/penilaian'
import DirectoryPage from '@/modules/directory/DirectoryPage'

const RETURN_URL_KEY = 'hrms_return_url'

const AppRouter = () => {
  const { isAuthenticated, isLoading } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  // Save the current URL when not authenticated (for redirect after login)
  useEffect(() => {
    if (!isLoading && !isAuthenticated && location.pathname !== '/') {
      sessionStorage.setItem(RETURN_URL_KEY, location.pathname + location.search)
    }
  }, [isLoading, isAuthenticated, location])

  // Redirect to saved URL after login
  useEffect(() => {
    if (isAuthenticated) {
      const returnUrl = sessionStorage.getItem(RETURN_URL_KEY)
      if (returnUrl) {
        sessionStorage.removeItem(RETURN_URL_KEY)
        navigate(returnUrl, { replace: true })
      }
    }
  }, [isAuthenticated, navigate])

  if (isLoading) {
    return <LoadingPage />
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    )
  }

  return (
    <Routes>
      <Route path="/" element={<Layout />}>
        <Route index element={<DashboardPage />} />
        <Route path="dashboard" element={<DashboardPage />} />

        {/* Leave Management Routes */}
        <Route path="leave/*" element={<LeaveRoutes />} />

        {/* LMS Routes */}
        <Route path="lms/*" element={<LMSRoutes />} />

        {/* Admin Routes */}
        <Route path="admin/*" element={<AdminRoutes />} />

        {/* Reimbursement Routes */}
        <Route path="reimbursement/*" element={<ReimbursementRoutes />} />

        {/* Learning & Development Routes */}
        <Route path="learning/*" element={<LearningRoutes />} />

        {/* Employee Information Routes */}
        <Route path="employee-info/*" element={<EmployeeInfoRoutes />} />

        {/* Lunch Expense Routes */}
        <Route path="lunch-expense/*" element={<LunchExpenseRoutes />} />

        {/* Penilaian Individu Routes */}
        <Route path="penilaian/*" element={<PenilaianRoutes />} />

        {/* Employee Directory */}
        <Route path="directory" element={<DirectoryPage />} />

        {/* Fallback route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  )
}

export default AppRouter
